import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import { useMemo, useState } from 'react';
import { useLazyGetBillableHoursByEmployeeAndYearReportQuery } from 'store/api/reportingTrends';
import {
    BillableHoursByEmployeeAndYearRow,
    BillableHoursByEmployeeAndYearTotals,
    EmployeeYearlyHoursRow,
} from 'types/reporting/trends';
import { t } from 'utils/getTranslations';
import { ChartPanel } from '../charts/ChartPanel';
import { formatChange, formatWhole } from '../charts/chartFormat';
import { ChartReportView } from '../charts/ChartReportView';
import { EmployeePeriodCharts } from '../charts/EmployeePeriodCharts';
import { FigureColumn, FigureTable } from '../charts/FigureTable';
import { KpiRow, KpiTile } from '../charts/KpiTile';
import { EMPLOYEE_NAME_SEARCH, employeeTrendFilters } from '../charts/trendFilters';
import { sameDayLastYear, yearToDateRange } from '../charts/yearToDate';
import { employeeLabel, formatReportNumber, pivotToneRule, ReportEmployeeLink, ReportTone } from '../reportCells';
import { ReportEntry } from '../reportCatalog';
import { ReportLayout } from '../ReportLayout';
import { quickFiltersFrom } from '../reportFilters';

const TONE: ReportTone = 'yellow';

type Row = BillableHoursByEmployeeAndYearRow;
type Totals = BillableHoursByEmployeeAndYearTotals;

const REPORT_KEY = 'billable-hours-by-employee-and-year';

function yearlyHours(row: EmployeeYearlyHoursRow): number {
    return row.billableHours;
}

function buildFilterConfig(): FilterConfig {
    const filters = employeeTrendFilters();
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        quickFilters: quickFiltersFrom(filters, { isActive: 'reporting.filter.is-active' }),
        searchBarSelectors: EMPLOYEE_NAME_SEARCH,
        filters,
    };
}

function buildColumns(): FigureColumn<Row>[] {
    return [
        {
            id: 'employeeName',
            header: t('reporting.column.employee'),
            cell: (row) => <ReportEmployeeLink employeeId={row.employeeId}>{employeeLabel(row)}</ReportEmployeeLink>,
        },
        { id: 'jobRole', header: t('reporting.column.job-role'), cell: (row) => row.jobRole ?? '–' },
        {
            id: 'billableHoursYtd',
            header: t('reporting.column.billable-hours-ytd'),
            numeric: true,
            cell: (row) => formatReportNumber(row.billableHoursYtd),
        },
        {
            id: 'billableHoursLytd',
            header: t('reporting.column.billable-hours-lytd'),
            numeric: true,
            cell: (row) => formatReportNumber(row.billableHoursLytd),
        },
        {
            id: 'billableHoursYtdVsLytdPct',
            header: t('reporting.column.ytd-vs-lytd'),
            numeric: true,
            cell: (row) => formatChange(row.billableHoursYtdVsLytdPct),
        },
        {
            id: 'billableHoursLy',
            header: t('reporting.column.billable-hours-ly'),
            numeric: true,
            cell: (row) => formatReportNumber(row.billableHoursLy),
        },
    ];
}

function employeeRowKey(row: Row): string {
    return `${row.employeeId}-${row.jobRoleId}-${row.coreTeamId}`;
}

function BillableHoursByEmployeeAndYearCharts({ rows, totals }: { rows: Row[]; totals: Totals }) {
    const [today] = useState(() => new Date());
    const columns = useMemo(() => buildColumns(), []);
    const lastYear = today.getFullYear() - 1;
    return (
        <>
            <KpiRow>
                <KpiTile
                    label={t('reporting.chart.billable-hours-ytd')}
                    value={formatWhole(totals.billableHoursYtd)}
                    caption={yearToDateRange(today)}
                />
                <KpiTile
                    label={t('reporting.chart.billable-hours-lytd')}
                    value={formatWhole(totals.billableHoursLytd)}
                    caption={yearToDateRange(sameDayLastYear(today))}
                />
                <KpiTile
                    label={t('reporting.chart.billable-hours-ly')}
                    value={formatWhole(totals.billableHoursLy)}
                    caption={`${lastYear}`}
                />
                <KpiTile
                    label={t('reporting.chart.ytd-vs-lytd')}
                    value={formatChange(totals.billableHoursYtdVsLytdPct)}
                    change={totals.billableHoursYtdVsLytdPct}
                    caption={t('reporting.chart.ytd-vs-lytd-caption')}
                />
            </KpiRow>
            <EmployeePeriodCharts
                tone={TONE}
                rows={totals.yearlyRows}
                period="year"
                value={yearlyHours}
                measure={t('reporting.column.billable-hours')}
                chartTitle={t(`reporting.${REPORT_KEY}.chart`)}
                tableTitle={t(`reporting.${REPORT_KEY}.table`)}
                total={t('reporting.chart.total-hours', { value: formatReportNumber(totals.billableHours) })}
            />
            <ChartPanel title={t(`reporting.${REPORT_KEY}.year-to-date`)} flush>
                <FigureTable
                    rows={rows}
                    columns={columns}
                    rowKey={employeeRowKey}
                    label={t(`reporting.${REPORT_KEY}.year-to-date`)}
                />
            </ChartPanel>
        </>
    );
}

/** The report starts on the active employees; the quick filter shows the choice and can clear it. */
const ACTIVE_EMPLOYEES = new URLSearchParams({ qf: 'isActive==true' }).toString();

function renderCharts(rows: Row[], totals: Totals) {
    return <BillableHoursByEmployeeAndYearCharts rows={rows} totals={totals} />;
}

export function BillableHoursByEmployeeAndYearReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetBillableHoursByEmployeeAndYearReportQuery();
    const filterConfig = useMemo(() => buildFilterConfig(), []);
    return (
        <ReportLayout
            report={report}
            toneLegend={[{ label: t('reporting.column.billable-hours'), rule: pivotToneRule(TONE) }]}
        >
            <ChartReportView<Row, Totals>
                analyticsSurface={`report-${REPORT_KEY}`}
                data={data}
                isFetching={isFetching}
                isError={isError}
                fetchData={fetchData}
                filterConfig={filterConfig}
                initialSearchParams={ACTIVE_EMPLOYEES}
                countTextIds={{ singular: 'reporting.chart.employee', plural: 'reporting.chart.employees' }}
                hasFigures
            >
                {renderCharts}
            </ChartReportView>
        </ReportLayout>
    );
}
